'use client';

import type { CSSProperties, FC, PropsWithChildren } from 'react';
import React from 'react';
import type { ResponsiveProperty } from '../../../utils/responsive';
import { lengthValue, resolveResponsive } from '../../../utils/responsive';
import type { SpacingType } from '../../../utils/spacing';

export type FlexGap = SpacingType;

export interface FlexProps {
  children?: React.ReactNode;
  className?: string;
  id?: string;
  display?: ResponsiveProperty<'flex' | 'inline-flex' | 'none'>;
  flexDirection?: ResponsiveProperty<CSSProperties['flexDirection']>;
  flexWrap?: ResponsiveProperty<CSSProperties['flexWrap']>;
  justifyContent?: ResponsiveProperty<CSSProperties['justifyContent']>;
  alignItems?: ResponsiveProperty<CSSProperties['alignItems']>;
  alignContent?: ResponsiveProperty<CSSProperties['alignContent']>;
  gap?: ResponsiveProperty<FlexGap>;
  height?: ResponsiveProperty<CSSProperties['height']>;
  minHeight?: ResponsiveProperty<CSSProperties['minHeight']>;
  style?: CSSProperties;
}

export const Flex: FC<PropsWithChildren<FlexProps>> = ({
  display,
  flexDirection,
  flexWrap,
  justifyContent,
  alignItems,
  alignContent,
  gap,
  height,
  minHeight,
  style,
  ...props
}) => {
  const composed: CSSProperties = {
    display: resolveResponsive(display) ?? 'flex',
  };
  const set = (key: keyof CSSProperties, value: string | undefined) => {
    if (value !== undefined) composed[key] = value as never;
  };

  set('flexDirection', resolveResponsive(flexDirection));
  set('flexWrap', resolveResponsive(flexWrap));
  set('justifyContent', resolveResponsive(justifyContent));
  set('alignItems', resolveResponsive(alignItems));
  set('alignContent', resolveResponsive(alignContent));
  set('gap', lengthValue(resolveResponsive(gap)));
  set('height', lengthValue(resolveResponsive(height)));
  set('minHeight', lengthValue(resolveResponsive(minHeight)));

  return <div style={{ ...composed, ...style }} {...props} />;
};
